/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BuilderNavigationService } from '@gh/builder-shared';
import { BuilderPermission } from '@gh/builder-shared-data';
import { EntityInputDialogConfig } from '@gh/common-internal-shared';
import { getRouteInitData } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, NotificationService, WaitingService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$, switchMap$ } from '@gh/rx/operators';

import { assign } from 'lodash';

import { ChecklistListBusinessService } from '../../resources/checklist-business.service';
import { ChecklistItem } from '../../resources/checklist.model';
import { ChecklistDetailsDialog } from './checklist-details-dialog.route';

@Component({
  selector: 'gh-complete-sales-list-tab',
  template: require('./checklist-tab.component.html'),
  styles: [require('./checklist-tab.component.scss')],
})
export class ChecklistListTabComponent implements OnInit {
  checklist: ChecklistItem[];

  constructor(private route: ActivatedRoute,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              private dialogService: DialogService,
              private navigationService: BuilderNavigationService,
              private checklistBusiness: ChecklistListBusinessService) {
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    assign(this, getRouteInitData(this.route));
  }

  onDropCheckItem(event: CdkDragDrop<ChecklistItem>): void {
    moveItemInArray(this.checklist, event.previousIndex, event.currentIndex);
    this.checklistBusiness.updateOrder(this.checklist).pipe(
      (this.waitingService.operator()))
      .subscribe(
        (checklist: ChecklistItem[]) => {
          this.checklist = checklist;
        },
        (error) => this.notificationService.processAll(error),
      );
  }

  onDelete(checklistItem: ChecklistItem): void {
    this.checklistBusiness.get(checklistItem.id).pipe(
      (this.waitingService.operator()),
      switchMap$(() => this.dialogService.confirmation(yesNoConfirmation('msg_record_delete'))),
      filter$(yesAnswer),
      switchMap$(() => this.checklistBusiness.delete(checklistItem.id)))
      .subscribe(() => {
        this.checklist = this.checklist.filter((item) => item.id !== checklistItem.id);
      });
  }

  onCreate(): void {
    this.dialogService.openWithInput(ChecklistDetailsDialog, <EntityInputDialogConfig<ChecklistItem>>{
      titleId: 'lbl_complete_checklist',
      messageId: 'lbl_complete_checklist_create_edit',
      placeholderId: 'lbl_title',
      field: 'name',
      required: true,
      maxLength: 128,
      permissionEdit: BuilderPermission.Setup.Sales.Checklist.CREATE,
      showTextArea: true,
      onSave: (checklistItem: ChecklistItem) => this.checklistBusiness.create(checklistItem),
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((item: any) => {
        this.checklist.push(item);
        this.notificationService.success('msg_created_successfully');
      }, this.notificationService.processAll); // tslint:disable-line:align
  }

  onEdit(checklistItem: ChecklistItem): void {
    this.dialogService.openWithInput(ChecklistDetailsDialog, <EntityInputDialogConfig<ChecklistItem>>{
      titleId: 'lbl_complete_checklist',
      titleParams: { 'checklistItem_name': checklistItem.name },
      messageId: 'lbl_complete_checklist_create_edit',
      messageParams: { 'checklistItem_name': checklistItem.name },
      placeholderId: 'lbl_title',
      field: 'name',
      required: true,
      maxLength: 128,
      permissionEdit: BuilderPermission.Setup.Sales.Checklist.EDIT,
      showTextArea: true,
      id: checklistItem.id,
      onSave: (item: ChecklistItem) => this.checklistBusiness.update(item),
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((item: any) => {
        checklistItem.name = item.name;
        this.notificationService.success('msg_saved_successfully');
      }); // tslint:disable-line:align
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      label: label ? label : 'lbl_complete_checklist',
      entityType: entityType ? entityType : 'COMPLETE_SALE_CHECKLIST',
      entityId: entityId ? entityId : undefined,
    });
  }
}
